import { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { FiFileText, FiSearch, FiShield } from "react-icons/fi";
import { Button, showToast } from "../../../../components/shared";
import { useProjectFiles } from "../../../../lib/react-query/files";
import { useCreateAIChatGrant } from "../../../../lib/react-query/aiChat";
import { cn } from "../../../../lib/cn";
import { extractBackendErrorMessage } from "../../../../utils/utils";

const fileLabel = (file) =>
  file?.original_name ||
  file?.original_filename ||
  file?.download_name ||
  file?.name ||
  file?.filename ||
  `File ${file?.id}`;

export const PermissionPicker = ({
  isOpen,
  onClose,
  sessionId,
  project,
  initialGrant,
}) => {
  const closeButtonRef = useRef(null);
  const [includeProject, setIncludeProject] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [ttlHours, setTtlHours] = useState(24);
  const { mutateAsync: createGrant, isPending } = useCreateAIChatGrant();
  const { data: filesData, isLoading: filesLoading } = useProjectFiles(
    project?.id,
    { search, page_size: 50 },
    isOpen && !!project?.id,
  );

  const files = useMemo(() => {
    const items = Array.isArray(filesData?.items) ? filesData.items : filesData || [];
    return Array.isArray(items) ? items : [];
  }, [filesData]);

  useEffect(() => {
    if (!isOpen) return;
    const activeSources = initialGrant?.sources || [];
    setSelectedFileIds(new Set(activeSources.map((source) => Number(source.file))));
    setIncludeProject(Boolean(project?.id || initialGrant?.project));
    setTtlHours(24);
  }, [isOpen, initialGrant, project]);

  const toggleFile = (fileId) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const canSubmit = includeProject || selectedFileIds.size > 0;

  const handleSubmit = async () => {
    if (!sessionId || !canSubmit) return;
    try {
      await createGrant({
        sessionId,
        payload: {
          project_id: includeProject && project?.id ? Number(project.id) : null,
          file_ids: Array.from(selectedFileIds),
          ttl_hours: Number(ttlHours) || 24,
        },
      });
      showToast("Access granted", "success", { position: "top-center" });
      onClose?.();
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to grant access"), "error", {
        position: "top-center",
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      backdrop="blur"
      size="2xl"
      initialFocus={closeButtonRef}
      classNames={{ base: "bg-white border border-surface-border rounded-xl" }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 text-primary">
          <FiShield aria-hidden="true" />
          Manage file access
        </ModalHeader>
        <ModalBody className="gap-4">
          <section aria-label="Project access" className="rounded-lg border border-surface-border p-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span>
                <span className="block text-sm font-semibold text-darkgrey">
                  {project?.title || project?.name || "Project context"}
                </span>
                <span className="block text-xs text-grey">
                  Project metadata and saved knowledge.
                </span>
              </span>
              <input
                aria-label="Include project context"
                type="checkbox"
                checked={includeProject}
                onChange={(e) => setIncludeProject(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
            </label>
          </section>

          <section aria-label="File access" className="rounded-lg border border-surface-border p-3">
            <div className="flex items-center gap-2 rounded-lg border border-surface-border px-3 py-2">
              <FiSearch className="text-grey" aria-hidden="true" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search files"
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div className="mt-3 max-h-56 overflow-y-auto space-y-1">
              {filesLoading ? (
                <div className="flex justify-center py-6"><Spinner size="sm" /></div>
              ) : files.length ? (
                files.map((file) => {
                  const fileId = Number(file.id);
                  return (
                    <label
                      key={file.id}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-2 py-2 text-sm cursor-pointer",
                        selectedFileIds.has(fileId) ? "bg-primary/5 text-primary" : "hover:bg-surface",
                      )}
                    >
                      <input
                        aria-label={`Include ${fileLabel(file)}`}
                        type="checkbox"
                        checked={selectedFileIds.has(fileId)}
                        onChange={() => toggleFile(fileId)}
                        className="h-4 w-4 accent-primary"
                      />
                      <FiFileText className="shrink-0" aria-hidden="true" />
                      <span className="truncate">{fileLabel(file)}</span>
                    </label>
                  );
                })
              ) : (
                <p className="py-5 text-center text-xs text-grey">No files found.</p>
              )}
            </div>
          </section>

          <details className="rounded-lg border border-surface-border p-3">
            <summary className="cursor-pointer text-sm font-medium text-darkgrey">Advanced</summary>
            <label className="mt-3 block text-xs font-medium text-mediumgrey">
              Access expires after
              <input
                type="number"
                min="1"
                max="168"
                value={ttlHours}
                onChange={(e) => setTtlHours(e.target.value)}
                className="mt-1 w-28 rounded-lg border border-surface-border px-2 py-1 text-sm"
              />
              <span className="ml-2 font-normal text-grey">hours</span>
            </label>
          </details>

          <p className="text-xs text-mediumgrey">
            Optional: choose context here before chatting. If you skip this, DraftFlow can search file names and ask for approval inline before reading file content.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            ref={closeButtonRef}
            className="rounded-lg border border-surface-border bg-white text-mediumgrey"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="rounded-lg bg-primary text-white disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
          >
            {isPending ? "Saving..." : "Save permissions"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
